import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { CheckCircleIcon, MailIcon, DownloadIcon, ShieldCheckIcon, ClockIcon, QuoteIcon, GiftIcon } from 'lucide-react';
const steps = [{
  number: '1',
  icon: MailIcon,
  title: 'CHECK YOUR EMAIL',
  description: 'Look for the subject line: "Your 72-Hour Urban Blueprint is Here". If you don\'t see it within 5 minutes, check your spam or promotions folder.'
}, {
  number: '2',
  icon: DownloadIcon,
  title: 'DOWNLOAD YOUR FILES',
  description: "Inside you'll find the full PDF Blueprint and the 2026 Urban Preparedness Checklist. Save them somewhere you can access fast."
}, {
  number: '3',
  icon: ShieldCheckIcon,
  title: 'WHITE-LIST OUR EMAIL',
  description: "Over the next 72 hours, we'll send you critical implementation tips to put your Blueprint into action. Make sure our emails land in your primary inbox."
}];
const testimonials = [{
  quote: 'I set up my apartment in 2 days. The peace of mind is priceless.',
  author: 'Mark',
  location: 'Chicago'
}, {
  quote: 'No more bunker stress. My wife and I finally feel in control.',
  author: 'Sarah',
  location: 'Dallas'
}, {
  quote: 'The water strategy alone was worth 10x the price. Game changer.',
  author: 'James',
  location: 'Phoenix'
}];
// Animation Variants
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const slideUpItem = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
const slideRightItem = {
  hidden: {
    opacity: 0,
    x: -20
  },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
export function ThankYouPage() {
  useMetaPixel();
  return <div className="min-h-screen w-full bg-gradient-to-b from-tactical-900 to-tactical-800 font-body text-white relative overflow-x-hidden selection:bg-accent selection:text-white pb-16" data-id="element-1">
      {/* Subtle Background Glow */}
      <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-accent-glow blur-[120px] rounded-full pointer-events-none z-0" data-id="element-2" />

      {/* SECTION 1: CELEBRATION HEADER BAR */}
      <motion.div initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      type: 'spring',
      stiffness: 100,
      damping: 20
    }} className="sticky top-0 z-50 w-full bg-accent py-3 shadow-lg shadow-accent/20" data-id="element-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2" data-id="element-4">
          <CheckCircleIcon className="w-5 h-5 text-white" data-id="element-5" />
          <span className="font-heading text-sm md:text-base font-bold text-white uppercase tracking-widest" data-id="element-6">
            YOUR FAMILY'S SAFETY SETUP HAS BEGUN!
          </span>
        </div>
      </motion.div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 relative z-10 space-y-24" data-id="element-7">
        {/* SECTION 2: HERO / WELCOME */}
        <section className="text-center" data-id="element-8">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="mb-12" data-id="element-9">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-wider mb-6 leading-tight" data-id="element-10">
              Congratulations. You just made the single most important decision
              for your family's security in 2026.
            </h1>
            <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-11">
              Most people wait for a crisis to react — you just took the lead.
              You are now part of the top 1% of urban families who won't be
              caught off guard.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4,
          ease: 'easeOut'
        }} className="relative max-w-3xl mx-auto" data-id="element-12">
            <div className="absolute -inset-0.5 bg-gradient-to-b from-accent/20 to-transparent rounded-xl blur-sm opacity-50" data-id="element-13" />
            <img src="https://cdn.magicpatterns.com/uploads/oVf8rLHjdNhqE8yy4PDGoG/private_openrouter-generated-image-1773435473441.png" alt="The Urban Autonomy Blueprint Product Suite" className="relative w-full h-auto rounded-lg shadow-[0_0_40px_rgba(232,114,12,0.15)] border border-tactical-500/50 object-cover" data-id="element-14" />
          </motion.div>
        </section>

        {/* SECTION 3: ACCESS INSTRUCTIONS */}
        <section data-id="element-15">
          <motion.h2 initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          amount: 0.3
        }} className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider text-center mb-12" data-id="element-16">
            HOW TO ACCESS YOUR <span className="text-accent" data-id="element-17">BLUEPRINT</span>{' '}
            NOW
          </motion.h2>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="max-w-2xl mx-auto space-y-6" data-id="element-18">
            {steps.map(step => {
            const Icon = step.icon;
            return <motion.div key={step.number} variants={slideRightItem} className="flex flex-col sm:flex-row items-start gap-5 p-6 bg-tactical-700/80 backdrop-blur-sm rounded-lg border border-tactical-500/30 shadow-lg" data-id="element-19">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-accent to-accent-dark flex items-center justify-center shadow-lg shadow-accent/20 border border-accent-light/20" data-id="element-20">
                    <span className="font-heading text-2xl font-bold text-white" data-id="element-21">
                      {step.number}
                    </span>
                  </div>
                  <div className="flex-1 pt-1 sm:pt-0" data-id="element-22">
                    <div className="flex items-center gap-3 mb-2" data-id="element-23">
                      <Icon className="w-5 h-5 text-accent-light" data-id="element-24" />
                      <h3 className="font-heading text-xl font-bold text-white uppercase tracking-wide" data-id="element-25">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-gray-300 leading-relaxed text-sm md:text-base" data-id="element-26">
                      {step.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </section>

        {/* SECTION 4: QUICK START */}
        <motion.section initial={{
        opacity: 0,
        scale: 0.98
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mx-auto" data-id="element-27">
          <div className="relative overflow-hidden rounded-lg bg-tactical-700 border-l-4 border-l-accent shadow-2xl shadow-accent/10" data-id="element-28">
            {/* Subtle pulse effect on border */}
            <div className="absolute top-0 left-0 w-1 h-full bg-accent-light animate-pulse opacity-50" data-id="element-29" />

            <div className="p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start md:items-center" data-id="element-30">
              <div className="flex-shrink-0 bg-tactical-800 p-4 rounded-full border border-tactical-500/50" data-id="element-31">
                <ClockIcon className="w-8 h-8 text-accent-light" data-id="element-32" />
              </div>
              <div data-id="element-33">
                <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-wide text-white mb-3" data-id="element-34">
                  DO THIS IN THE NEXT 10 MINUTES:
                </h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-35">
                  Open{' '}
                  <span className="text-accent-light font-semibold" data-id="element-36">
                    Chapter 1: The Invisible Water Strategy
                  </span>
                  . This is the #1 mistake urban families make. Fixing this
                  tonight will put you ahead of 90% of your neighbors
                  immediately.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* SECTION 5: TESTIMONIALS */}
        <section data-id="element-37">
          <motion.h2 initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          amount: 0.3
        }} className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-wide text-center mb-10" data-id="element-38">
            WHAT OTHER PARENTS ARE SAYING
          </motion.h2>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-39">
            {testimonials.map((t, i) => <motion.div key={i} variants={slideUpItem} className="bg-tactical-700/60 border border-tactical-500/20 rounded-lg p-6 flex flex-col relative overflow-hidden" data-id="element-40">
                <QuoteIcon className="absolute top-4 right-4 w-12 h-12 text-accent/10 -z-10" data-id="element-41" />
                <p className="text-gray-300 italic mb-6 flex-grow relative z-10" data-id="element-42">
                  "{t.quote}"
                </p>
                <div className="border-t border-tactical-500/30 pt-4 mt-auto" data-id="element-43">
                  <p className="font-heading text-sm text-accent-light uppercase tracking-wider" data-id="element-44">
                    {t.author}, {t.location}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </section>

        {/* SECTION 6: SURPRISE BONUS */}
        <motion.section initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        type: 'spring',
        stiffness: 80,
        damping: 20
      }} className="max-w-2xl mx-auto" data-id="element-45">
          <div className="bg-gradient-to-br from-tactical-800 to-tactical-700 rounded-xl p-8 border border-dashed border-accent/50 relative overflow-hidden shadow-xl shadow-accent/5" data-id="element-46">
            {/* Decorative corner glow */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-accent-glow rounded-full blur-2xl" data-id="element-47" />

            <div className="flex flex-col items-center text-center relative z-10" data-id="element-48">
              <div className="bg-tactical-900/50 p-3 rounded-full mb-4 border border-tactical-500/30" data-id="element-49">
                <GiftIcon className="w-8 h-8 text-accent-light" data-id="element-50" />
              </div>
              <span className="font-heading text-xs text-accent-light uppercase tracking-widest mb-2" data-id="element-51">
                🎁 SURPRISE BONUS
              </span>
              <h2 className="font-heading text-xl md:text-2xl font-bold uppercase text-white mb-4 tracking-wide" data-id="element-52">
                You've Unlocked Something Extra
              </h2>
              <p className="text-gray-300 leading-relaxed" data-id="element-53">
                As a welcome gift, I've unlocked the{' '}
                <span className="text-accent-light font-semibold" data-id="element-54">
                  '2026 Supply Chain Map'
                </span>{' '}
                for you. You'll find it inside your members' area.
              </p>
            </div>
          </div>
        </motion.section>

        {/* SECTION 7: SUPPORT FOOTER */}
        <motion.footer initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true,
        amount: 0.5
      }} className="text-center pt-10 pb-8" data-id="element-55">
          <div className="h-px w-32 bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mb-8 opacity-50" data-id="element-56" />
          
          
          <p className="text-xs text-gray-500 font-medium tracking-wider uppercase" data-id="element-57">
            &copy; 2026 Urban Autonomy Blueprint. All rights reserved.
          </p>
        </motion.footer>
      </div>
    </div>;
}